import api from './config';

export const distillApi = async (thought: string) => {
  const response = await api.post('/api/distill', { thought });
  return response.data;
};

export const generateApi = async (payload: {
  distilledInsight: string;
  topic: string;
  toneTags: string[];
  hook: string;
  angle: string;
  userPov?: string;
  voiceFingerprint?: string;
  // RI-012 fix: optional raw thought passed through to the prompt so Gemini
  // can preserve user's specifics, named entities, emotional arc, and
  // first-person voice that distillation strips out. Backend gracefully
  // falls back to legacy behaviour when omitted.
  rawThought?: string;
}) => {
  const response = await api.post('/api/generate', payload);
  return response.data;
};

export const saveInsightApi = async (payload: {
  rawThought: string;
  distilledInsight: string;
  topic: string;
  toneTags: string[];
  hook: string;
  pov?: { agree: string; disagree: string };
  angle: string;
  outputs: Record<string, string>;
  exploreSessionId?: string;
}) => {
  const response = await api.post('/api/insights', payload);
  return response.data;
};

export const getInsightsApi = async (page = 1, pageSize = 20) => {
  const response = await api.get(`/api/insights?page=${page}&pageSize=${pageSize}`);
  return response.data;
};

export const getInsightByIdApi = async (id: string) => {
  const response = await api.get(`/api/insights/${id}`);
  return response.data;
};

export const deleteInsightApi = async (id: string) => {
  const response = await api.delete(`/api/insights/${id}`);
  return response.data;
};

export const submitFeedbackApi = async (packId: string, format: string, vote: 'up' | 'down') => {
  const response = await api.post(`/api/distribution-packs/${packId}/feedback`, { format, vote });
  return response.data;
};

export const exploreMessageApi = async (payload: {
  sessionId: string | null;
  message: string;
  history: { role: string; content: string; timestamp: string }[];
}) => {
  const response = await api.post('/api/explore/message', payload);
  return response.data;
};

export const exploreSummariseApi = async (payload: {
  sessionId: string;
  insightText: string;
  history: { role: string; content: string; timestamp: string }[];
}) => {
  const response = await api.post('/api/explore/summarise', payload);
  return response.data;
};

export const createShareApi = async (payload: {
  distilledInsight: string;
  topic?: string;
  toneTags?: string[];
  hook?: string;
  pov?: { agree: string; disagree: string };
  outputs: Record<string, string>;
  angle?: string;
}): Promise<{ token: string; url: string }> => {
  const response = await api.post('/api/share', payload);
  return response.data;
};

export const getShareApi = async (token: string) => {
  const response = await api.get(`/api/share/${token}`);
  return response.data;
};

export const getPovApi = async () => {
  const response = await api.get('/api/user/pov');
  return response.data;
};

export const updatePovApi = async (savedPov: string) => {
  const response = await api.put('/api/user/pov', { savedPov });
  return response.data;
};

export const getMeApi = async () => {
  const response = await api.get('/api/user/me');
  return response.data;
};

export const getSessionApi = async (installId: string) => {
  const response = await api.get(`/api/session?install_id=${encodeURIComponent(installId)}`);
  return response.data as {
    exploreSessionId: string;
    messages: { role: string; content: string; timestamp: string }[];
    insights: any[];
    updatedAt: string;
  } | null;
};

export const saveSessionApi = async (payload: {
  install_id: string;
  exploreSessionId?: string | null;
  messages: { role: string; content: string; timestamp: string }[];
  insights: any[];
}) => {
  const response = await api.post('/api/session', payload);
  return response.data;
};

export const reviseApi = async (payload: {
  currentOutput: string;
  /** Original post before any revisions — clean anchor for regeneration. */
  originalPost?: string;
  /** All instructions already applied, in order — sent so LLM can honour every
   *  constraint simultaneously and not re-introduce what was corrected before. */
  previousInstructions?: string[];
  format: string;
  angle: string;
  distilledInsight: string;
  topic: string;
  instruction: string;
}) => {
  const response = await api.post('/api/revise', payload);
  return response.data as { output: string };
};

export const getInsightHistoryApi = async (installId: string, pageSize = 5) => {
  const response = await api.get(`/api/insights?install_id=${encodeURIComponent(installId)}&pageSize=${pageSize}`);
  return response.data as {
    id: number;
    distilledInsight: string;
    topic: string;
    toneTags: string[];
    hook: string;
    angle: string;
    shareToken: string | null;
    createdAt: string;
  }[];
};
